import { useMemo, type ReactNode } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPage,
  DrawerPages,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  OptionPanelNavRow,
  OptionPanelSection,
  Text,
  useDrawerPages,
  type IconName,
  type Omittable,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import type { QuickFilterOption } from "#app/components/filters/quick-inputs/quick-filter-options";
import { QuickOptionList } from "#app/components/filters/quick-inputs/quick-option-list";
import type { EntityData } from "#app/lib/entity-visuals";

/**
 * The member lists' quick filters on a PHONE: one door instead of a row of
 * labeled chips.
 *
 * The desktop toolbar reads `Status ⌄ Role ⌄ Reports to ⌄` — each chip keeps
 * its label on the face, because the label IS what the chip asks. A phone's
 * toolbar has no width for three labeled chips beside Group, Sort and the
 * search: the run wrapped onto a second line, the one thing a toolbar band
 * must never do. Icon-only facets were not the answer either — a status
 * glyph and a role glyph mean nothing alone, and a held value would need a
 * readout that reopens the wrap.
 *
 * So the facets fold behind the projects list's phone grammar: a filter chip
 * at the icon footprint whose corner sticker counts the applied facets, and a
 * bottom sheet listing one row per facet in the option-panel grammar
 * (`icon label … value ›`). A row drills into a page of the sheet's stack
 * holding that facet's values — the single-select list the desktop menu
 * offers, with the same counts beside each option — and picking pops back to
 * the list with the row now reading the pick, the way the desktop radio menu
 * closes on its pick. Clear all foots the sheet as a full-width outline
 * button while anything is applied, the phone Filters sheet's rule: a thumb
 * scanning a sheet needs the reset to stand out and to be a 40px target.
 */

export type MemberFacetOption = Omittable<{
  value: string;
  label: string;
  /** Set when the value IS an entity (a team, a manager), so the row and the
   *  list render its tag. */
  entity?: EntityData;
  /** Sub-heading within the page's list. */
  group?: string;
  /** Extra match text for the page's search — a member's email. */
  keywords?: string[];
  /** Members picking this value would show; absent while unknown. */
  count?: number;
}>;

export type MemberFacet = Omittable<{
  /** The page key; unique across the sheet. */
  key: string;
  label: string;
  icon: IconName;
  /** The row's faint readout while nothing is picked. */
  unfiltered: string;
  options: MemberFacetOption[];
  /** The pick as an entity when `options` cannot name it — a facet over a
   *  windowed list, whose page is `renderPicker`. */
  valueEntity?: EntityData | null;
  /** The page's own picker in place of the flat option list, for a facet
   *  whose values page and search the server (Reports to). Calls `onPicked`
   *  to pop back to the facet list. */
  renderPicker?: (onPicked: () => void) => ReactNode;
  value: string | null;
  onChange: (value: string | null) => void;
}>;

type MemberFilterSheetProps = Omittable<{
  facets: MemberFacet[];
  /** How many facets hold a value — the chip's sticker and the footer's
   *  gate. */
  activeCount: number;
  onClearAll: () => void;
}>;

/** The root list's floor. The popup sizes to its content, and a drilled page
 *  gets the root's height only — three facet rows would hand the Reports-to
 *  page too little for its pinned search and a roster to scroll under it,
 *  so the root holds roughly half a phone screen whatever it lists. */
const ROOT_MIN_HEIGHT = 320;

export function MemberFilterSheet({
  facets,
  activeCount,
  onClearAll,
}: MemberFilterSheetProps) {
  return (
    // `full`, matching the projects Filters sheet.
    <Drawer size="full">
      <DrawerTrigger
        render={<FilterToggle size="xs" icon="filter" count={activeCount} />}
      />
      {/* Page fill, matching the projects Filters sheet. */}
      <DrawerPopup showBar surface="background">
        {/* The stack the rows push their value pages onto; the header and
            panel below are its root page. */}
        <DrawerPages>
          <DrawerHeader>
            <DrawerTitle>Filters</DrawerTitle>
          </DrawerHeader>
          <DrawerPanel className="px-0 pt-3">
            <Box minHeight={ROOT_MIN_HEIGHT}>
              <FacetRows facets={facets} />
            </Box>
          </DrawerPanel>
          {activeCount > 0 ? (
            <DrawerFooter>
              <Button
                variant="outline"
                color="neutral"
                size="xl"
                fullWidth
                onClick={onClearAll}
              >
                <Icon name="x" />
                Clear all filters
              </Button>
            </DrawerFooter>
          ) : null}
        </DrawerPages>
      </DrawerPopup>
    </Drawer>
  );
}

/** The root list — one nav row per facet — and the pages the rows push.
 *  Its own component so it can read the stack the popup mounts around it. */
function FacetRows({ facets }: { facets: MemberFacet[] }) {
  const pages = useDrawerPages();
  return (
    <>
      <OptionPanelSection>
        {facets.map((facet) => (
          <OptionPanelNavRow
            key={facet.key}
            label={facet.label}
            icon={facet.icon}
            value={<FacetValue facet={facet} />}
            onClick={() => pages?.open(facet.key)}
          />
        ))}
      </OptionPanelSection>
      {/* The pages render beside the rows that own their data and portal
          into the stack's layer over the list. */}
      {facets.map((facet) => (
        <FacetPage key={facet.key} facet={facet} onPicked={() => pages?.back()} />
      ))}
    </>
  );
}

/** What a facet row reads on its trailing edge: the pick as the VALUE — a
 *  team's tile, a manager's avatar, a status's word — or the unfiltered
 *  state in the faint rung, since nothing applied is still a fact. */
function FacetValue({ facet }: { facet: MemberFacet }) {
  if (facet.value == null) {
    return (
      <Text size="sm" variant="faint" truncate={1}>
        {facet.unfiltered}
      </Text>
    );
  }
  const picked = facet.options.find((option) => option.value === facet.value);
  const pickedEntity = facet.valueEntity ?? picked?.entity;
  if (pickedEntity) {
    return (
      <EntityTag
        entity={pickedEntity}
        size="xs"
        weight="regular"
        interactive={false}
      />
    );
  }
  // A value the list cannot name (a role since deleted) keeps the row honest
  // about being set under the facet's own name.
  return (
    <Text size="sm" variant="muted" truncate={1}>
      {picked?.label ?? facet.label}
    </Text>
  );
}

/** A facet's values as a page: the shared phone option list, read through
 *  single-select algebra — one value held, pressing it again lets go. */
function FacetPage({
  facet,
  onPicked,
}: {
  facet: MemberFacet;
  onPicked: () => void;
}) {
  const options = useMemo(
    () => facet.options.map(singleSelectOption),
    [facet.options],
  );
  return (
    <DrawerPage
      value={facet.key}
      title={facet.label}
      action={
        facet.value != null ? (
          <Button size="xs" variant="ghost" onClick={() => facet.onChange(null)}>
            <Text size="xs" variant="muted">
              Clear
            </Text>
          </Button>
        ) : null
      }
    >
      <DrawerPanel variant="workspace">
        {facet.renderPicker ? (
          facet.renderPicker(onPicked)
        ) : (
          <QuickOptionList
            options={options}
            value={facet.value}
            onChange={(next) => {
              facet.onChange(typeof next === "string" ? next : null);
              onPicked();
            }}
            label={facet.label}
          />
        )}
      </DrawerPanel>
    </DrawerPage>
  );
}

function singleSelectOption(option: MemberFacetOption): QuickFilterOption {
  const { value, ...rest } = option;
  return {
    ...rest,
    key: value,
    selected: (held) => held === value,
    toggle: (held) => (held === value ? null : value),
  };
}
